import React from 'react';

import { FormattedMessage } from 'react-intl';
import { DefaultTheme } from 'styled-components';

import { CoreUtils, ReactTableEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, ColumnHelper, createColumnHelper } from '@tanstack/react-table';

import commonMessages from '~/core/commonMessages';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { LandingGame, Penalty } from '../../types';

import { centeredText, penaltyStyle, TeamContainer } from './styles';

const LEFT_ALIGNED = 'left-aligned';

const smallColumnWidth = 60;
const columnHelper: ColumnHelper<Penalty> = createColumnHelper<Penalty>();

const TEAM_COLUMN = (game: LandingGame): ColumnDef<Penalty> =>
    columnHelper.display({
        id: 'team',
        size: smallColumnWidth,
        maxSize: smallColumnWidth,
        header: () => <FormattedMessage {...commonMessages.tableHeaders.long.team} />,
        cell: (data) => {
            const penaltyTeam = parseValueFromLocalizedAttribute(data.row.original.teamAbbrev);
            const team = game.awayTeam.abbrev === penaltyTeam ? game.awayTeam : game.homeTeam;

            return (
                <span aria-label={parseValueFromLocalizedAttribute(team.name)}>
                    {team.logo ? (
                        <TeamContainer
                            logoUrlOverride={team.logo}
                            team={{ triCode: team.abbrev }}
                            displayName={false}
                        />
                    ) : (
                        penaltyTeam
                    )}
                </span>
            );
        },
        meta: {
            headerStyle: centeredText,
            scope: ReactTableEnums.Scopes.ROW,
            style: centeredText,
        },
    });

const TIME_COLUMN: ColumnDef<Penalty> = columnHelper.display({
    id: 'time',
    size: smallColumnWidth,
    maxSize: smallColumnWidth,
    header: () => <FormattedMessage {...commonMessages.tableHeaders.long.gameEventTime} />,
    cell: (data) => data.row.original.timeInPeriod,
    meta: {
        headerStyle: centeredText,
        style: centeredText,
    },
});

const PENALTY_COLUMN = (theme: DefaultTheme): ColumnDef<Penalty> =>
    columnHelper.display({
        id: 'penalty',
        size: 120,
        header: () => <FormattedMessage {...commonMessages.tableHeaders.long.penalty} />,
        cell: ({ row }) => {
            const { descKey, committedByPlayer, drawnBy, duration } = row.original;
            const translatedPenalty = commonMessages.penaltyDetailDescriptions[descKey];
            const committedByPlayerName = `${parseValueFromLocalizedAttribute(
                committedByPlayer?.firstName
            )} ${parseValueFromLocalizedAttribute(committedByPlayer?.lastName)}`;
            const drawnByPlayerName = `${parseValueFromLocalizedAttribute(
                drawnBy?.firstName
            )} ${parseValueFromLocalizedAttribute(drawnBy?.lastName)}`;

            return (
                <FormattedMessage
                    description="A sentence describing a committed penalty. Ex. Brad Marchand #63 2 minutes for Slashing Conor Geekie #14"
                    defaultMessage="<committedByText>{committedByPlayer} (#{committedByPlayerNumber}) </committedByText>{duration} minutes for {description} <drawnByText>{drawnBy} (#{drawnByNumber})</drawnByText>"
                    values={{
                        committedByPlayer: committedByPlayerName,
                        description: translatedPenalty ? (
                            <CoreUtils.SafeFormattedMessage message={translatedPenalty} />
                        ) : (
                            descKey
                        ),
                        drawnBy: drawnByPlayerName,
                        committedByText: (text) => committedByPlayer !== undefined && text,
                        drawnByText: (text) => drawnBy !== undefined && text,
                        duration,
                        committedByPlayerNumber: committedByPlayer?.sweaterNumber as number,
                        drawnByNumber: drawnBy?.sweaterNumber as number,
                    }}
                />
            );
        },
        meta: {
            className: LEFT_ALIGNED,
            headerClassName: LEFT_ALIGNED,
            headerStyle: penaltyStyle(theme),
            style: penaltyStyle(theme),
        },
    });

const NO_PENALTY_NULL_COLUMN = (isTeamColumn: boolean): ColumnDef<Penalty> =>
    columnHelper.display({
        id: isTeamColumn ? 'team' : 'time',
        size: smallColumnWidth,
        maxSize: smallColumnWidth,
        header: () =>
            isTeamColumn ? (
                <FormattedMessage {...commonMessages.tableHeaders.long.team} />
            ) : (
                <FormattedMessage {...commonMessages.tableHeaders.long.gameEventTime} />
            ),
        cell: () => <FormattedMessage {...commonMessages.statsDescriptions.noStatIndicator} />,
        meta: {
            headerStyle: centeredText,
            style: centeredText,
        },
    });

const NO_PENALTY_COLUMN = (theme: DefaultTheme): ColumnDef<Penalty> => ({
    ...PENALTY_COLUMN(theme),
    cell: () => <FormattedMessage {...commonMessages.eventDescriptions.noPenaltiesTaken} />,
});

export const columns = (game: LandingGame, theme: DefaultTheme): ColumnDef<Penalty>[] => [
    TEAM_COLUMN(game),
    TIME_COLUMN,
    PENALTY_COLUMN(theme),
];

export const noPenaltyColumns = (theme: DefaultTheme): ColumnDef<Penalty>[] => [
    NO_PENALTY_NULL_COLUMN(true),
    NO_PENALTY_NULL_COLUMN(false),
    NO_PENALTY_COLUMN(theme),
];
